import React, {useEffect, useState, useRef, type ReactElement} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {DownloadPanelUpselling} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling';
import styles from './download-panel-upselling-wrapper.module.scss';

interface DownloadPanelUpsellingWrapperProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  isVisible: boolean;
  onClose: () => void;
}

export function DownloadPanelUpsellingWrapper({containerRef, wrapperRef, isVisible, onClose}: DownloadPanelUpsellingWrapperProps): ReactElement | null {
  const [upsellingTop, setUpsellingTop] = useState<number | undefined>(undefined);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!isVisible) {
      setUpsellingTop(undefined);
      return;
    }

    const targetEl = containerRef.current ?? wrapperRef.current;
    if (!targetEl) {
      setUpsellingTop(undefined);
      return;
    }

    const calculatePosition = (): void => {
      requestAnimationFrame(() => {
        const el = containerRef.current ?? wrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setUpsellingTop(rect.bottom + 12);
      });
    };

    const timeoutId = setTimeout(() => {
      calculatePosition();
    }, 0);

    const resizeObserver = new ResizeObserver(() => {
      calculatePosition();
    });

    resizeObserver.observe(targetEl);

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);

    return (): void => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isVisible, containerRef, wrapperRef]);

  useEffect(() => {
    if (isVisible) {
      const id = setTimeout(() => {
        isMountedRef.current = true;
      }, 0);
      return (): void => {
        clearTimeout(id);
      };
    }
    isMountedRef.current = false;
    return undefined;
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isMountedRef.current ? (
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0}}
          style={{
            top: upsellingTop,
          }}
          className={styles.webUpselling}
        >
          <DownloadPanelUpselling onClose={onClose} />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
